/** Labels and helpers of the media links (B13). */

import type { Job, MediaItem, MediaStatus } from "@/lib/api";

export const mediaKey = ["media"];

export const isMediaJob = (job: Job) => job.kind === "media_check" || job.kind === "media_fetch";

/** Check state of a link, and „none“ for titles without links (catalogue filter). */
export const MEDIA_STATUS: Record<
  MediaStatus | "none",
  { label: string; variant: "success" | "secondary" | "destructive" | "outline" }
> = {
  error: { label: "Fehler", variant: "destructive" },
  warning: { label: "Warnung", variant: "secondary" },
  unchecked: { label: "Ungeprüft", variant: "outline" },
  ok: { label: "OK", variant: "success" },
  none: { label: "Keine Medien", variant: "outline" },
};

export const mediaStatusLabel = (value: string) =>
  MEDIA_STATUS[value as MediaStatus | "none"]?.label ?? value;

/** Finding codes of `onix_core::media` and the worker. */
export const FINDINGS: Record<string, string> = {
  http: "Nicht erreichbar (HTTP-Status)",
  unreachable: "Server nicht erreichbar",
  blocked: "Adresse nicht erlaubt",
  bad_url: "Ungültiger Link",
  redirects: "Zu viele Weiterleitungen",
  too_large: "Datei zu groß",
  empty: "Leere Datei",
  wrong_kind: "Falsche Art (z. B. Webseite statt Bild)",
  unknown_format: "Format unbekannt",
  declared_format: "Format weicht von der Angabe ab",
  too_small: "Bild zu klein",
  declared_size: "Pixelgröße weicht von der Angabe ab",
  no_dimensions: "Pixelgröße nicht lesbar",
  declared_bytes: "Dateigröße weicht von der Angabe ab",
  store_failed: "Kopie nicht gespeichert",
};

/** List 158, the usual values; others show their code. */
export const CONTENT_TYPES: Record<string, string> = {
  "01": "Cover",
  "02": "Rückseite",
  "03": "Cover mit Rücken",
  "04": "Autorenfoto",
  "07": "Verlagslogo",
  "15": "Leseprobe",
  "16": "Rezension",
  "23": "Innenansicht",
  "24": "Video",
  "25": "Audio",
};

export const contentTypeLabel = (code: string | null) =>
  code ? (CONTENT_TYPES[code] ?? `Art ${code}`) : "Medium";

/** Front cover (list 158 code 01) as an image? */
export const isCover = (m: MediaItem) => m.content_type === "01";

/** A stored copy the browser can show (raster images, see the worker). */
export const previewable = (m: MediaItem) =>
  m.stored && ["jpeg", "png", "gif", "webp"].includes(m.format ?? "");

/** „1.600 × 2.400 px“ */
export function pixels(m: Pick<MediaItem, "width" | "height">): string | null {
  if (!m.width || !m.height) return null;
  return `${m.width.toLocaleString("de-DE")} × ${m.height.toLocaleString("de-DE")} px`;
}

/** Title of a media job. */
export function mediaJobTitle(job: Job): string {
  const fetch = job.kind === "media_fetch";
  switch (job.payload.scope) {
    case "evaluate":
      return "Medien neu bewerten";
    case "all":
      return fetch ? "Alle Medien abrufen" : "Alle Medien prüfen";
    default:
      return fetch ? "Neue Medien abrufen" : "Neue Medien prüfen";
  }
}
